import { useRef, useState } from 'react';
import {
  detectParser,
  formatMoney,
  importCsv,
  parseReceiptText,
  planRestore,
  receiptToTransaction,
  validateBackup,
  type Backup,
  type BackupPreview,
  type CsvImportResult,
  type Transaction,
} from '@wis/shared';
import { useApp } from '../state/AppContext.js';
import { saveTransactions } from '../db/repository.js';
import { db } from '../db/database.js';
import {
  Button,
  Field,
  Notice,
  Segmented,
  Select,
  TextArea,
  sourceLabel,
} from '../components/primitives.js';
import { extractTextFromFile, type ExtractionProgress } from '../import/extract.js';
import { StatementImport } from './StatementImport.js';

type Mode = 'receipt' | 'statement' | 'file' | 'backup';

export function Import(): JSX.Element {
  const [mode, setMode] = useState<Mode>('receipt');

  return (
    <div className="stack-lg" style={{ marginTop: 'var(--space-3)' }}>
      <Segmented
        label="What are you importing?"
        value={mode}
        options={[
          { value: 'receipt', label: 'Receipt' },
          { value: 'statement', label: 'Statement' },
          { value: 'file', label: 'CSV' },
          { value: 'backup', label: 'Backup' },
        ]}
        onChange={setMode}
      />

      {mode === 'receipt' ? <ReceiptImport /> : null}
      {mode === 'statement' ? <StatementImport /> : null}
      {mode === 'file' ? <CsvImport /> : null}
      {mode === 'backup' ? <BackupRestore /> : null}
    </div>
  );
}

/* ---------- receipts ---------- */

function ReceiptImport(): JSX.Element {
  const { accounts, categories, rules, merchantHistory, settings, toast } = useApp();
  const [text, setText] = useState('');
  const [issuer, setIssuer] = useState<'auto' | 'maybank' | 'tng'>('auto');
  const [account, setAccount] = useState('');
  const [result, setResult] = useState<ReturnType<typeof receiptToTransaction> | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [progress, setProgress] = useState<ExtractionProgress | null>(null);
  const cancelRef = useRef<{ cancelled: boolean }>({ cancelled: false });
  const fileInput = useRef<HTMLInputElement>(null);

  const detected = text ? detectParser(text) : undefined;

  function handleParse(source = text): void {
    setError(undefined);
    setResult(null);

    const parsed = parseReceiptText(source, {
      timeZone: settings.timeZone,
      ...(issuer === 'auto' ? {} : { parser: issuer }),
    });

    if (!parsed.ok) {
      setError(parsed.reason);
      return;
    }

    setResult(
      receiptToTransaction(parsed, {
        source: parsed.parser === 'tng' ? 'tng-receipt' : 'maybank-receipt',
        accountLabel: account || settings.defaultAccountLabel,
        categories,
        rules,
        history: merchantHistory.map((entry) => ({
          merchant: entry.merchant,
          categoryId: entry.categoryId,
          count: entry.count,
        })),
        defaultCategoryId: settings.defaultCategoryId,
      }),
    );
  }

  async function handleFile(file: File): Promise<void> {
    setError(undefined);
    setResult(null);
    cancelRef.current = { cancelled: false };

    try {
      const extracted = await extractTextFromFile(file, {
        onProgress: setProgress,
        signal: cancelRef.current,
      });
      setProgress(null);
      if (!extracted.text.trim()) {
        setError(extracted.reason ?? 'No text could be read from that file.');
        return;
      }
      setText(extracted.text);
      handleParse(extracted.text);
    } catch (failure) {
      setProgress(null);
      setError(failure instanceof Error ? failure.message : 'That file could not be read.');
    }
  }

  async function handleSave(): Promise<void> {
    if (!result) return;
    await saveTransactions([result.transaction]);
    toast(
      result.transaction.reviewStatus === 'needs-review'
        ? 'Saved to the review inbox'
        : `Saved ${formatMoney(result.transaction.amountSen)}`,
    );
    setText('');
    setResult(null);
  }

  return (
    <section className="stack">
      <Notice tone="info">
        Paste the text of a MAE, Maybank or Touch &rsquo;n Go receipt, or pick a screenshot or PDF.
        Everything is read on this device. Nothing is uploaded, and no image is stored unless you
        turn that on in Settings.
      </Notice>

      <Field label="Issuer">
        {(props) => (
          <Select
            {...props}
            value={issuer}
            onChange={(event) => setIssuer(event.target.value as typeof issuer)}
          >
            <option value="auto">
              Detect automatically{detected ? ` (looks like ${detected.label})` : ''}
            </option>
            <option value="maybank">MAE / Maybank2u</option>
            <option value="tng">Touch &rsquo;n Go eWallet</option>
          </Select>
        )}
      </Field>

      <Field label="Paid with" hint="A receipt does not say which of your accounts it came from.">
        {(props) => (
          <Select {...props} value={account} onChange={(event) => setAccount(event.target.value)}>
            <option value="">{settings.defaultAccountLabel} (default)</option>
            {accounts
              .filter((entry) => entry.label !== settings.defaultAccountLabel)
              .map((entry) => (
                <option key={entry.id} value={entry.label}>
                  {entry.label}
                </option>
              ))}
          </Select>
        )}
      </Field>

      <Field
        label="Receipt text"
        hint="Copy the receipt from the app and paste it here, or use the file picker below."
      >
        {(props) => (
          <TextArea
            {...props}
            rows={8}
            value={text}
            placeholder={"Touch 'n Go eWallet\nPayment Successful\n- RM 12.90\nMerchant: ..."}
            onChange={(event) => setText(event.target.value)}
          />
        )}
      </Field>

      <div className="row">
        <input
          ref={fileInput}
          type="file"
          accept="image/*,application/pdf,text/plain"
          className="visually-hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void handleFile(file);
            event.target.value = '';
          }}
        />
        <Button className="grow" onClick={() => fileInput.current?.click()}>
          Choose a file
        </Button>
        <Button variant="primary" className="grow" disabled={!text.trim()} onClick={() => handleParse()}>
          Read receipt
        </Button>
      </div>

      {progress ? (
        <div className="card">
          <p className="small">{progress.message}</p>
          <div className="budget__track" style={{ marginTop: 'var(--space-2)' }}>
            <div className="budget__fill" style={{ width: `${Math.round(progress.ratio * 100)}%` }} />
          </div>
          <Button
            variant="quiet"
            block
            style={{ marginTop: 'var(--space-3)' }}
            onClick={() => {
              cancelRef.current.cancelled = true;
              setProgress(null);
            }}
          >
            Cancel
          </Button>
        </div>
      ) : null}

      {error ? <Notice tone="danger">{error}</Notice> : null}

      {result ? (
        <div className="card stack">
          <h3 className="section__title">What was read</h3>
          <dl className="stack">
            <ReadRow label="Amount" value={formatMoney(result.transaction.amountSen)} certain />
            <ReadRow
              label="Merchant"
              value={result.transaction.merchantNormalized || 'not found'}
              certain={!result.missingFields.includes('merchant')}
            />
            <ReadRow
              label="Date"
              value={result.transaction.occurredAt.slice(0, 16).replace('T', ' ')}
              certain={!result.missingFields.includes('date')}
            />
            <ReadRow
              label="Type"
              value={result.transaction.type}
              certain={!result.missingFields.includes('type')}
            />
            <ReadRow label="Category" value={result.transaction.categoryId} certain={false} />
          </dl>

          {result.warnings.length > 0 ? (
            <Notice tone="warning">{result.warnings.join(' ')}</Notice>
          ) : null}

          <p className="small muted">
            Read with {Math.round(result.transaction.importConfidence * 100)}% confidence.{' '}
            {result.transaction.reviewStatus === 'needs-review'
              ? 'This will go to the review inbox so you can check it.'
              : 'This looks complete.'}
          </p>

          <Button variant="accent" block onClick={handleSave}>
            Save as {sourceLabel(result.transaction.source)}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function ReadRow({
  label,
  value,
  certain,
}: {
  label: string;
  value: string;
  certain: boolean;
}): JSX.Element {
  return (
    <div className="row row--between">
      <dt className="small muted">{label}</dt>
      <dd className="small" style={{ margin: 0, textAlign: 'right' }}>
        {value} {certain ? null : <span className="badge badge--review">check</span>}
      </dd>
    </div>
  );
}

/* ---------- csv ---------- */

function CsvImport(): JSX.Element {
  const { categories, rules, merchantHistory, settings, toast } = useApp();
  const [result, setResult] = useState<CsvImportResult | null>(null);
  const [fileName, setFileName] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  async function handleFile(file: File): Promise<void> {
    const text = await file.text();
    setFileName(file.name);
    setResult(
      importCsv(text, {
        categories,
        rules,
        history: merchantHistory.map((entry) => ({
          merchant: entry.merchant,
          categoryId: entry.categoryId,
          count: entry.count,
        })),
        defaultCategoryId: settings.defaultCategoryId,
        accountLabel: settings.defaultAccountLabel,
        timeZone: settings.timeZone,
        currency: settings.currency,
      }),
    );
  }

  async function handleSave(): Promise<void> {
    if (!result) return;
    const count = await saveTransactions(result.transactions);
    toast(`Imported ${count} ${count === 1 ? 'payment' : 'payments'}`);
    setResult(null);
    setFileName('');
  }

  const total = result?.transactions.reduce((sum, transaction) => sum + transaction.amountSen, 0) ?? 0;

  return (
    <section className="stack">
      <Notice tone="info">
        Pick a CSV exported from your bank or another app. The columns are matched by name; anything
        that cannot be read is listed by line number rather than dropped quietly.
      </Notice>

      <input
        ref={fileInput}
        type="file"
        accept=".csv,text/csv,text/plain"
        className="visually-hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleFile(file);
          event.target.value = '';
        }}
      />
      <Button variant="primary" block onClick={() => fileInput.current?.click()}>
        Choose a CSV file
      </Button>

      {result ? (
        <div className="card stack">
          <h3 className="section__title">{fileName}</h3>
          <p className="small muted">
            {result.rowCount} rows read · {result.transactions.length} ready to import ·{' '}
            {result.errors.length} skipped
          </p>
          <p className="small">
            Columns matched:{' '}
            {Object.entries(result.mapping)
              .map(([field, index]) => `${field} → ${result.header[index] ?? `column ${index + 1}`}`)
              .join(', ') || 'none'}
          </p>

          {result.transactions.length > 0 ? (
            <>
              <p className="small">
                Total of the rows that will be imported:{' '}
                <span className="money">{formatMoney(total)}</span>
              </p>
              <div className="scroll-x">
                <table className="data-table">
                  <caption className="visually-hidden">First rows to be imported</caption>
                  <thead>
                    <tr>
                      <th scope="col">Date</th>
                      <th scope="col">Merchant</th>
                      <th scope="col">Type</th>
                      <th scope="col">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.transactions.slice(0, 8).map((transaction: Transaction) => (
                      <tr key={transaction.id}>
                        <td>{transaction.occurredAt.slice(0, 10)}</td>
                        <td>{transaction.merchantNormalized || '-'}</td>
                        <td>{transaction.type}</td>
                        <td className="money">{formatMoney(transaction.amountSen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          {result.errors.length > 0 ? (
            <details>
              <summary className="small">{result.errors.length} rows skipped</summary>
              <ul className="stack" style={{ marginTop: 'var(--space-2)' }}>
                {result.errors.slice(0, 20).map((error) => (
                  <li key={error.line} className="tiny faint">
                    Line {error.line}: {error.message}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          <Button
            variant="accent"
            block
            disabled={result.transactions.length === 0}
            onClick={handleSave}
          >
            Import {result.transactions.length}{' '}
            {result.transactions.length === 1 ? 'payment' : 'payments'}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

/* ---------- backup ---------- */

function BackupRestore(): JSX.Element {
  const { transactions, toast } = useApp();
  const [preview, setPreview] = useState<BackupPreview | null>(null);
  const [backup, setBackup] = useState<Backup | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [skipped, setSkipped] = useState(0);
  const [mode, setMode] = useState<'merge' | 'replace'>('merge');
  const [typed, setTyped] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  async function handleFile(file: File): Promise<void> {
    setIssues([]);
    setPreview(null);
    setBackup(null);

    const validation = validateBackup(await file.text());
    if (!validation.ok) {
      setIssues(validation.issues.map((issue) => `${issue.path || 'file'}: ${issue.message}`));
      return;
    }

    setBackup(validation.backup);
    setPreview(validation.preview);
    setSkipped(validation.skipped.length);
  }

  const plan = backup ? planRestore(backup, transactions, mode) : null;

  async function handleRestore(): Promise<void> {
    if (!backup || !plan) return;

    await saveTransactions([...plan.toInsert, ...plan.toUpdate], { fromServer: false });

    if (mode === 'replace' && plan.toRemove.length > 0) {
      const now = new Date().toISOString();
      await saveTransactions(
        plan.toRemove.map((transaction) => ({ ...transaction, deletedAt: now, updatedAt: now })),
      );
    }

    if (backup.categories?.length) await db.categories.bulkPut(backup.categories);
    if (backup.accounts?.length) await db.accounts.bulkPut(backup.accounts);
    if (backup.merchantRules?.length) await db.merchantRules.bulkPut(backup.merchantRules);

    toast(`Restored ${plan.toInsert.length + plan.toUpdate.length} records`);
    setBackup(null);
    setPreview(null);
    setTyped('');
  }

  const needsPhrase = mode === 'replace';
  const canRestore = Boolean(plan) && (!needsPhrase || typed === 'REPLACE');

  return (
    <section className="stack">
      <Notice tone="info">
        Restore a backup exported from this app. The file is checked first, and nothing is written
        until you have seen exactly what it contains.
      </Notice>

      <input
        ref={fileInput}
        type="file"
        accept=".json,application/json"
        className="visually-hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleFile(file);
          event.target.value = '';
        }}
      />
      <Button variant="primary" block onClick={() => fileInput.current?.click()}>
        Choose a backup file
      </Button>

      {issues.length > 0 ? (
        <Notice tone="danger">
          This file was not restored. {issues.slice(0, 3).join(' · ')}
        </Notice>
      ) : null}

      {preview && plan ? (
        <div className="card stack">
          <h3 className="section__title">What is in this backup</h3>
          <ul className="stack small">
            <li>{preview.transactionCount} transactions</li>
            <li>{preview.categoryCount} categories, {preview.accountCount} accounts, {preview.ruleCount} rules</li>
            <li>
              {preview.earliest?.slice(0, 10) ?? '-'} to {preview.latest?.slice(0, 10) ?? '-'}
            </li>
            <li>Exported {preview.exportedAt.slice(0, 10)}</li>
            {skipped > 0 ? (
              <li className="money--negative">{skipped} records could not be read and will be skipped</li>
            ) : null}
          </ul>

          <Segmented
            label="How to restore"
            value={mode}
            options={[
              { value: 'merge', label: 'Merge' },
              { value: 'replace', label: 'Replace' },
            ]}
            onChange={(next) => {
              setMode(next);
              setTyped('');
            }}
          />

          <Notice tone={mode === 'replace' ? 'warning' : 'info'}>
            {mode === 'merge'
              ? `Adds ${plan.toInsert.length} new records and updates ${plan.toUpdate.length} that are newer in the backup. Nothing on this device is removed.`
              : `Adds ${plan.toInsert.length}, updates ${plan.toUpdate.length}, and removes ${plan.toRemove.length} records that are not in the backup.`}
          </Notice>

          {needsPhrase ? (
            <Field label="Type REPLACE to confirm">
              {(props) => (
                <input
                  {...props}
                  className="input"
                  value={typed}
                  autoCapitalize="characters"
                  autoComplete="off"
                  onChange={(event) => setTyped(event.target.value)}
                />
              )}
            </Field>
          ) : null}

          <Button variant="accent" block disabled={!canRestore} onClick={handleRestore}>
            Restore
          </Button>
        </div>
      ) : null}
    </section>
  );
}
